import {
  SELECT_BOARD_PIN_COUNT_THRESHOLD,
  USER_BOARD_RECOMMEND_THRESHOLD,
} from "@/constants/pinCollectThreshold";
import useAppStore from "@/context/store";
import { useRealtimePinCount } from "@/utils/hooks/useRealtimePinCount";
import {
  getRecommendedRelatedBoards,
  matchBoards,
} from "@/utils/textSimilarity";
import { useMemoizedFn } from "ahooks";
import { useCallback } from "react";

/**
 * 画板匹配结果接口
 */
export interface BoardMatchingResult {
  // 悬停时的推荐画板（最匹配的用户画板）
  recommendedBoard?: any;
  // 下拉选择时的推荐画板列表
  recommendedBoards: any[];
  // 推荐的同在画板列表（用于创建新画板）
  recommendedRelatedBoards: any[];
  // 是否使用用户画板推荐
  useUserBoardRecommendation: boolean;
}

/**
 * 画板匹配 Hook
 */
export function useBoardMatching() {
  const {
    lastBoards: userBoards,
    setLastBoards,
    refreshLastBoards,
    lastBoardsReady,
  } = useAppStore(
    ({ lastBoards, setLastBoards, refreshLastBoards, lastBoardsReady }) => ({
      lastBoards,
      setLastBoards,
      refreshLastBoards,
      lastBoardsReady,
    })
  );
  const realtimePinCount = useRealtimePinCount();
  /**
   * 判断是否为需要显示画板选择器的新用户（没有画板且没有采集过图片）
   */
  const isNewUser = useMemoizedFn((): boolean => {
    return (
      lastBoardsReady &&
      userBoards.length === 0 &&
      realtimePinCount === SELECT_BOARD_PIN_COUNT_THRESHOLD
    );
  });

  /**
   * 确保用户画板已加载（如果需要的话）
   */
  const ensureUserBoardsLoaded = useMemoizedFn(async (): Promise<any[]> => {
    // 如果已经加载过用户画板数据，直接返回（无论是否为空）
    if (lastBoardsReady) {
      return userBoards || [];
    }

    // 如果还没有加载过，尝试刷新
    try {
      const boards = await refreshLastBoards();
      return boards || [];
    } catch (err) {
      console.error("刷新用户画板信息失败:", err);
      return [];
    }
  });

  /**
   * 匹配画板并生成推荐
   */
  const matchAndRecommend = useMemoizedFn(
    (relatedBoards: any[], userBoards: any[]): BoardMatchingResult => {
      if (!relatedBoards.length) {
        return {
          recommendedBoard: null,
          recommendedBoards: [],
          recommendedRelatedBoards: [],
          useUserBoardRecommendation: false,
        };
      }

      // 过滤掉数字、字数只有1个、以及「未整理」「待整理」等画板名
      const filteredRelatedBoards = relatedBoards.filter((board) => {
        const title = board.title || "";
        // 过滤掉纯数字、单个字符、只有1个字的标题，以及「未整理」「待整理」等
        const excludeKeywords = [
          "未整理",
          "待整理",
          "未分类",
          "未分",
          "未分类画板",
          "待分类",
          "临时",
          "测试",
          "temp",
          "test",
        ];
        const shouldExclude = excludeKeywords.some(
          (keyword) =>
            title.includes(keyword) ||
            title.toLowerCase().includes(keyword.toLowerCase())
        );

        return (
          title.length > 1 &&
          !/^\d+$/.test(title) &&
          !/^[a-zA-Z]$/.test(title) &&
          !/^[\u4e00-\u9fa5]$/.test(title) &&
          !shouldExclude
        );
      });

      // 先进行匹配，无论用户画板数量多少
      const matchResults = matchBoards(userBoards, filteredRelatedBoards);

      if (userBoards.length < USER_BOARD_RECOMMEND_THRESHOLD) {
        // 用户画板数 < 5，推荐同在画板名用于创建
        // 过滤掉已存在的用户画板名
        const existingTitles = userBoards
          .map((b) => b.title?.toLowerCase().trim())
          .filter(Boolean);
        const uniqueRelatedBoards = filteredRelatedBoards.filter((board) => {
          const title = board.title?.toLowerCase().trim();
          return title && !existingTitles.includes(title);
        });

        // 去重同名画板，使用 Map 确保唯一性
        const titleMap = new Map();
        const deduplicatedBoards = uniqueRelatedBoards.filter((board) => {
          const title = board.title?.toLowerCase().trim();
          if (!title) return false;
          if (titleMap.has(title)) return false;
          titleMap.set(title, true);
          return true;
        });

        const recommendedRelatedBoards = getRecommendedRelatedBoards(
          deduplicatedBoards,
          5
        );

        // 即使画板数 < 5，也要找到最佳匹配用于快采
        const bestMatch = matchResults.length > 0 ? matchResults[0] : null;
        const recommendedBoard =
          bestMatch && bestMatch.similarity > 0.1 ? bestMatch.board : null;

        return {
          recommendedBoard,
          recommendedBoards: [],
          recommendedRelatedBoards,
          useUserBoardRecommendation: false,
        };
      } else {
        // 用户画板数 >= 阈值，使用算法匹配结果
        // 过滤出有相似度的匹配结果（相似度 > 0）
        const validMatches = matchResults.filter(
          (match) => match.similarity > 0
        );

        if (validMatches.length > 0) {
          // 有有效匹配，精选推荐前2个用户画板
          const recommendedBoard = validMatches[0].board;
          const recommendedBoards = validMatches
            .slice(0, 2)
            .map((match) => match.board);

          return {
            recommendedBoard,
            recommendedBoards,
            recommendedRelatedBoards: [],
            useUserBoardRecommendation: true,
          };
        } else {
          // 没有任何有效匹配，推荐同在画板用于创建
          // 过滤掉已存在的用户画板名
          const existingTitles = userBoards
            .map((b) => b.title?.toLowerCase().trim())
            .filter(Boolean);
          const uniqueRelatedBoards = filteredRelatedBoards.filter((board) => {
            const title = board.title?.toLowerCase().trim();
            return title && !existingTitles.includes(title);
          });

          // 去重同名画板，使用 Map 确保唯一性
          const titleMap = new Map();
          const deduplicatedBoards = uniqueRelatedBoards.filter((board) => {
            const title = board.title?.toLowerCase().trim();
            if (!title) return false;
            if (titleMap.has(title)) return false;
            titleMap.set(title, true);
            return true;
          });

          const recommendedRelatedBoards = getRecommendedRelatedBoards(
            deduplicatedBoards,
            5
          );

          return {
            recommendedBoard: null,
            recommendedBoards: [],
            recommendedRelatedBoards,
            useUserBoardRecommendation: false,
          };
        }
      }
    }
  );

  /**
   * 为单个图片生成画板匹配结果
   */
  const getBoardMatchingForPin = useMemoizedFn(
    (
      pin: any,
      relatedBoards: any[],
      userBoards: any[]
    ): BoardMatchingResult => {
      if (!relatedBoards.length) {
        return {
          recommendedBoards: [],
          recommendedRelatedBoards: [],
          useUserBoardRecommendation: false,
        };
      }

      return matchAndRecommend(relatedBoards, userBoards);
    }
  );

  /**
   * 批量处理图片的画板匹配
   */
  const batchMatchBoards = useMemoizedFn(
    (
      pins: any[],
      relatedBoardsMap: Map<number, any[]>
    ): Map<number, BoardMatchingResult> => {
      const results = new Map<number, BoardMatchingResult>();

      pins.forEach((pin) => {
        if (pin.pin_id && relatedBoardsMap.has(pin.pin_id)) {
          const relatedBoards = relatedBoardsMap.get(pin.pin_id) || [];
          const matching = getBoardMatchingForPin(
            pin,
            relatedBoards,
            userBoards
          );
          results.set(pin.pin_id, matching);
        }
      });

      return results;
    }
  );

  /**
   * 获取缓存状态
   */
  const getCacheStatus = useCallback(() => {
    return {
      userBoardsCount: userBoards.length,
      isLoading: false,
      hasError: false,
    };
  }, [userBoards.length]);

  /**
   * 更新用户画板信息（当用户创建新画板时调用）
   */
  const updateUserBoards = useCallback(
    (newBoard: any, username?: string) => {
      setLastBoards((prev) => {
        // 确保 prev 是数组
        const prevArray = Array.isArray(prev) ? prev : [];
        const updated = [...prevArray];
        const existingIndex = updated.findIndex(
          (board) => board.board_id === newBoard.board_id
        );

        if (existingIndex >= 0) {
          updated[existingIndex] = newBoard;
        } else {
          updated.unshift(newBoard); // 新画板放在前面
        }

        return updated;
      });
    },
    [setLastBoards]
  );

  /**
   * 删除用户画板（当用户删除画板时调用）
   */
  const removeUserBoard = useCallback(
    (boardId: number, username?: string) => {
      setLastBoards((prev) => {
        // 确保 prev 是数组
        const prevArray = Array.isArray(prev) ? prev : [];
        return prevArray.filter((board) => board.board_id !== boardId);
      });
    },
    [setLastBoards]
  );

  return {
    userBoards,
    ensureUserBoardsLoaded,
    matchAndRecommend,
    getBoardMatchingForPin,
    batchMatchBoards,
    updateUserBoards,
    removeUserBoard,
    isNewUser,
    getCacheStatus,
    loading: false, // 不再需要 loading 状态，因为使用全局状态
    error: false, // 不再需要 error 状态，因为使用全局状态
  };
}
